import React, {
  useState,
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from 'react'
import { useFormContext } from 'react-hook-form'
import Cookies from 'js-cookie'
import * as XLSX from 'xlsx'
import { IconButton } from 'rsuite'
import InfoOutlineIcon from '@rsuite/icons/InfoOutline'

import CustomFileUploader from '../../../../../ui_reusable/CustomFileUploader'
import FileValidationModal from './FileValidationModal'
import FileEditValidationModal from './FileEditValidationModal'
import {
  ErrorMessage,
  Label,
} from '../../../../../ui_reusable/styles/CreateInputUtils.styles'
import { HStack, RequiredLabelStar } from '../styles/DatasetForm.styles'

import type { FileData } from '../../../../../ui_reusable/CustomFileUploader'
import type {
  DatasetCreateRequest,
  DatasetCreateRequestInterval,
} from '@/api/generated/models'

type FileInfo = {
  name: string
  blobFile: File | { name: string; isMock: boolean }
  fileKey: number
}

interface FileUploadSectionProps {
  isEdit?: boolean
  initialFile?: { name: string }
  isRequired?: boolean
}

export interface FileUploadSectionRef {
  getFileForSubmission: () => FileInfo | null
  reset: () => void
}

const COOKIE_NAME = 'hideFileValidation'
const COOKIE_NAME_EDIT = 'hideFileValidationEdit'

const acceptedFileTypes =
  '.csv, .xls, .xlsx, text/csv, application/csv, text/comma-separated-values, application/csv, application/excel, application/vnd.msexcel, text/anytext, application/vnd. ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

const FileUploadSection = forwardRef<
  FileUploadSectionRef,
  FileUploadSectionProps
>(({ isEdit = false, initialFile, isRequired }, ref) => {
  const {
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useFormContext<DatasetCreateRequest>()

  const [fileInfo, setFileInfo] = useState<FileInfo | null>(null)
  const [hasFileChanged, setHasFileChanged] = useState<boolean>(false)
  const [showInfoModal, setShowInfoModal] = useState<boolean>(false)

  const [hideValidationInfo, setHideValidationInfo] = useState<boolean>(
    () => Cookies.get(COOKIE_NAME) === 'true',
  )
  const [hideEditValidationInfo, setHideEditValidationInfo] = useState<boolean>(
    () => Cookies.get(COOKIE_NAME_EDIT) === 'true',
  )

  const customFileUploaderRef = useRef<any>(null)
  const hasInitializedRef = useRef<boolean>(false)

  // Effect to display the initial file in edit mode (only runs once on mount)
  useEffect(() => {
    if (isEdit && initialFile?.name && !hasInitializedRef.current) {
      hasInitializedRef.current = true
      const mockFile = { name: initialFile.name, isMock: true }
      setFileInfo({
        name: initialFile.name,
        blobFile: mockFile,
        fileKey: Date.now(),
      })
    }
  }, [isEdit, initialFile])

  // Expose functions to the parent component via ref
  useImperativeHandle(
    ref,
    () => ({
      getFileForSubmission: () => {
        // Only return fileInfo if it contains a real File object (not a mock)
        // Mock files are placeholders for displaying existing files in edit mode
        // and should never be submitted to the backend
        if (fileInfo && fileInfo.blobFile instanceof File) {
          return fileInfo
        }
        return null
      },
      reset: () => {
        setFileInfo(null)
        setHasFileChanged(false)
        if (customFileUploaderRef.current) {
          customFileUploaderRef.current.reset()
        }
      },
    }),
    [fileInfo],
  )

  /**
   * Converts an Excel date serial (number) to a JS Date.
   * Excel stores dates as number of days since 1899-12-31 (with a bug for 1900 leap year).
   * A commonly used conversion for modern dates (post 1900) is:
   *   new Date((serial - 25569) * 86400 * 1000)
   * 25569 is the number of days between 1899-12-31 and 1970-01-01.
   */
  const excelSerialToDate = (serial: number): Date => {
    // Guard for invalid serials
    if (isNaN(serial) || serial <= 0) return new Date(NaN)
    const jsTime = Math.round((serial - 25569) * 86400 * 1000)
    return new Date(jsTime)
  }

  /**
   * Try to parse a value from a spreadsheet cell into a Date.
   * Handles:
   *  - numbers (Excel serials)
   *  - numeric strings ("44455")
   *  - ISO strings ("2021-08-30")
   *  - common dd/mm/yyyy or dd-mm-yyyy formats
   */
  const parseDateCell = (value: any): Date | null => {
    if (value == null || value === '') return null

    // If it's already a Date
    if (value instanceof Date) {
      if (isNaN(value.getTime())) return null
      return value
    }

    // If it's a number -> treat as Excel serial (common with XLS/XLSX)
    if (typeof value === 'number') {
      const d = excelSerialToDate(value)
      if (!isNaN(d.getTime())) return d
      return null
    }

    // If it's a string that represents a number (Excel exported as text)
    if (typeof value === 'string') {
      const trimmed = value.trim()

      // pure numeric string (integer or float) -> treat as excel serial
      if (/^\d+(\.\d+)?$/.test(trimmed)) {
        const num = Number(trimmed)
        const d = excelSerialToDate(num)
        if (!isNaN(d.getTime())) return d
      }

      // Try Date.parse first (handles ISO yyyy-mm-dd, and many browser-acceptable formats)
      const parsed = Date.parse(trimmed)
      if (!isNaN(parsed)) return new Date(parsed)

      // Try common dd/mm/yyyy or dd-mm-yyyy
      // eslint-disable-next-line
      const dm = trimmed.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/)
      if (dm) {
        let day = Number(dm[1])
        let month = Number(dm[2])
        let year = Number(dm[3])
        if (year < 100) {
          // two-digit year heuristic: treat as 2000+
          year += 2000
        }
        // Construct date in local timezone
        const d = new Date(year, month - 1, day)
        if (!isNaN(d.getTime())) return d
      }

      // As a last resort, return null
      return null
    }

    return null
  }

  const detectFrequency = (rows: any[][]): 'daily' | 'weekly' | 'monthly' => {
    if (!rows || rows.length < 3) return 'monthly'
    const raw1 = rows[1][0]
    const raw2 = rows[2][0]

    try {
      const date1 = parseDateCell(raw1)
      const date2 = parseDateCell(raw2)

      if (!date1 || !date2) {
        console.error(
          'Could not parse date cells for frequency detection',
          raw1,
          raw2,
        )
        return 'monthly'
      }

      const differenceInTime = date2.getTime() - date1.getTime()
      const differenceInDays = Math.round(differenceInTime / (1000 * 3600 * 24))

      if (differenceInDays >= 28 && differenceInDays <= 31) return 'monthly'
      if (differenceInDays >= 7 && differenceInDays < 10) return 'weekly'
      if (differenceInDays === 1) return 'daily'
    } catch (error) {
      console.error('Could not parse dates to determine frequency.', error)
    }
    return 'monthly'
  }

  const extractDateRange = (
    rows: any[][],
  ): { startDate: string; endDate: string } | null => {
    if (!rows || rows.length < 2) return null

    try {
      // First date is in row 1, column 0 (row 0 is headers)
      const firstDateRaw = rows[1][0]
      const firstDate = parseDateCell(firstDateRaw)

      // Last date is in the last row, column 0
      const lastDateRaw = rows[rows.length - 1][0]
      const lastDate = parseDateCell(lastDateRaw)

      if (!firstDate || !lastDate) {
        console.error(
          'Could not parse date range from CSV',
          firstDateRaw,
          lastDateRaw,
        )
        return null
      }

      // Format as YYYY-MM-DD
      const formatDate = (date: Date): string => {
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
      }

      return {
        startDate: formatDate(firstDate),
        endDate: formatDate(lastDate),
      }
    } catch (error) {
      console.error('Error extracting date range:', error)
      return null
    }
  }

  const handleFileChange = async (files: FileData[]) => {
    if (!files || files.length === 0) {
      setFileInfo(null)
      return
    }

    const fileData = files[0]
    setFileInfo({
      name: fileData.name,
      blobFile: fileData.blobFile,
      fileKey: fileData.fileKey || Date.now(),
    })
    setHasFileChanged(true)
    clearErrors('file')

    const file = fileData.blobFile
    let data: ArrayBuffer | null = null

    try {
      data = await file.arrayBuffer()
      const workbook = XLSX.read(data)
      const firstSheetName = workbook.SheetNames[0]
      const worksheet = workbook.Sheets[firstSheetName]

      if (worksheet) {
        const rows: any[][] = XLSX.utils.sheet_to_json(worksheet, { header: 1 })
        if (rows && rows.length > 0) {
          const headers = rows[0]
          setValue('name', headers[1], { shouldValidate: true })
          const frequency = detectFrequency(rows)
          setValue('interval', frequency as DatasetCreateRequestInterval)

          // Extract date range for pairable API
          const dateRange = extractDateRange(rows)
          if (dateRange) {
            setValue('start_at', dateRange.startDate)
            setValue('end_at', dateRange.endDate)
          }
        }
      }
    } catch (error) {
      console.error('Error processing file:', error)
    } finally {
      // Explicit cleanup to prevent memory leaks with large files
      data = null
    }
  }

  const handleRemoveFile = () => {
    setFileInfo(null)
    // When removing, we reset the auto-filled name
    setValue('name', '')
    setHasFileChanged(true)
    if (!isEdit) {
      setError('file', { type: 'manual', message: 'File is required' })
    }
  }

  const handleHideInfoChanged = (hide: boolean) => {
    if (hide) {
      Cookies.set(COOKIE_NAME, 'true', { expires: 7 })
    } else {
      Cookies.remove(COOKIE_NAME)
    }
    setHideValidationInfo(hide)
  }

  const handleHideEditInfoChanged = (hide: boolean) => {
    if (hide) {
      Cookies.set(COOKIE_NAME_EDIT, 'true', { expires: 7 })
    } else {
      Cookies.remove(COOKIE_NAME_EDIT)
    }
    setHideEditValidationInfo(hide)
  }

  const handleInfoClick = (e: React.MouseEvent) => {
    e.preventDefault()
    setShowInfoModal(true)
  }

  return (
    <div>
      <Label>
        File
        {isRequired && <RequiredLabelStar>*</RequiredLabelStar>}
      </Label>
      <HStack>
        <CustomFileUploader
          ref={customFileUploaderRef}
          onFileSelect={handleFileChange}
          onRemove={handleRemoveFile}
          acceptedTypes={acceptedFileTypes}
          showBtn={true}
          initialFile={fileInfo as any}
          ValidationModal={
            isEdit
              ? hideEditValidationInfo
                ? null
                : FileEditValidationModal
              : hideValidationInfo
                ? null
                : FileValidationModal
          }
          onHideInFutureChanged={
            isEdit ? handleHideEditInfoChanged : handleHideInfoChanged
          }
        />
        <IconButton
          type="button"
          onClick={handleInfoClick}
          icon={<InfoOutlineIcon />}
        />
      </HStack>
      <ErrorMessage>{errors.file?.message}</ErrorMessage>

      <FileValidationModal
        open={showInfoModal}
        handleClose={() => setShowInfoModal(false)}
        mode="information"
      />
    </div>
  )
})

FileUploadSection.displayName = 'FileUploadSection' // for better debugging
export default FileUploadSection
